import type { Food, MealType, RecipeSummary } from '@fitness/api-client';
import { defaultMeal, MEAL_OPTIONS } from '@fitness/core';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Plus, ScanBarcode } from 'lucide-react';
import { useState } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { FoodSearch } from '@/components/food/FoodSearch';
import { ManualSheet } from '@/components/food/ManualSheet';
import { QuantitySheet, type QuantityTarget } from '@/components/food/QuantitySheet';
import { RecentEntries, type RecentItem } from '@/components/food/RecentEntries';
import { RecipePicker } from '@/components/food/RecipePicker';
import { formatDayHeading, todayIso } from '@/components/food/format';
import { foodAddSchema, type FoodAddSearch } from '@/components/food/searchParams';
import { cn } from '@/lib/cn';
import { readLastMeal, rememberMeal } from '@/lib/food/mealPreference';
import { FOOD_DIARY_COPY, mealLabel } from '@/lib/i18n/nutrition';
import { saveMealEntry } from '@/lib/offline/meals';

export const Route = createFileRoute('/_app/food/add')({
  validateSearch: (search: Record<string, unknown>): FoodAddSearch => foodAddSchema.parse(search),
  component: AddFoodPage,
});

const TABS = [
  { key: 'search', label: 'Buscar' },
  { key: 'recent', label: 'Recientes' },
  { key: 'recipes', label: 'Recetas' },
  { key: 'manual', label: 'Manual' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Añadir algo al diario.
 *
 * Cuatro caminos hacia lo mismo, ordenados por lo que se usa: buscar (con el
 * escáner a un toque), repetir lo de siempre, tirar de receta y, si nada de eso
 * vale, apuntarlo a mano.
 */
function AddFoodPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const date = search.date ?? todayIso();
  const [meal, setMeal] = useState<MealType>(() =>
    defaultMeal({ date, now: new Date(), queryMeal: search.meal, lastMeal: readLastMeal() }),
  );

  const [tab, setTab] = useState<TabKey>('search');
  const [target, setTarget] = useState<QuantityTarget | null>(null);

  function backToDiary(): void {
    void navigate({ to: '/food', search: { date } });
  }

  /** Repite una comida anterior tal cual, sin volver a preguntar la cantidad. */
  async function repeat(item: RecentItem): Promise<void> {
    await saveMealEntry({
      date,
      meal,
      foodId: item.foodId,
      recipeId: item.recipeId,
      label: item.label,
      quantity: item.quantity,
      unit: item.unit,
      totals: item.totals,
    });
    backToDiary();
  }

  return (
    <>
      <PageHeader
        title={FOOD_DIARY_COPY.add}
        subtitle={formatDayHeading(date)}
        action={
          <Link
            to="/food"
            search={{ date }}
            aria-label="Volver al diario"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div
        role="group"
        aria-label={FOOD_DIARY_COPY.chooseMeal}
        data-tour="meal-picker"
        className="mb-4 flex flex-wrap gap-1 rounded-xl bg-surface-raised p-1"
      >
        {(meal === 'other' ? ([...MEAL_OPTIONS, 'other'] as const) : MEAL_OPTIONS).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={meal === option}
            onClick={() => {
              setMeal(option);
              rememberMeal(option);
            }}
            className={cn(
              'min-h-11 flex-1 rounded-lg px-1 text-sm font-semibold transition-colors',
              meal === option
                ? 'bg-brand text-brand-ink'
                : 'text-ink-muted hover:bg-surface-overlay active:bg-surface-overlay',
            )}
          >
            {mealLabel(option)}
          </button>
        ))}
      </div>

      <div
        role="tablist"
        aria-label="Cómo añadir"
        className="mb-4 flex gap-1 rounded-xl bg-surface-raised p-1"
      >
        {TABS.map((option) => (
          <button
            key={option.key}
            type="button"
            role="tab"
            aria-selected={option.key === tab}
            onClick={() => setTab(option.key)}
            className={cn(
              'min-h-11 flex-auto rounded-lg px-1 text-sm font-semibold transition-colors xs:flex-1',
              option.key === tab
                ? 'bg-brand text-brand-ink'
                : 'text-ink-muted hover:bg-surface-overlay active:bg-surface-overlay',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'search' ? (
        // El recorrido guiado señala el buscador con sus resultados.
        <div data-tour="food-search">
          <FoodSearch
            autoFocus
            onSelect={(food: Food) => setTarget({ kind: 'food', food })}
            toolbar={
              <Link
                to="/food/scan"
                search={{ date, meal }}
                data-tour="food-scan"
                className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-line bg-surface-raised text-sm font-semibold text-ink transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
              >
                <ScanBarcode className="size-4" aria-hidden="true" />
                Escanear código de barras
              </Link>
            }
            emptyAction={
              <Link
                to="/food/foods/new"
                search={{ date, meal }}
                className="flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface-raised px-4 text-sm font-semibold text-ink"
              >
                <Plus className="size-4" aria-hidden="true" />
                Crear alimento
              </Link>
            }
          />
        </div>
      ) : null}

      {tab === 'recent' ? <RecentEntries enabled onSelect={(item) => void repeat(item)} /> : null}

      {tab === 'recipes' ? (
        <RecipePicker
          onSelect={(recipe: RecipeSummary) =>
            setTarget({
              kind: 'recipe',
              recipe: {
                id: recipe.id,
                name: recipe.name,
                imageUrl: recipe.imageUrl,
                perServing: {
                  kcal: recipe.kcalPerServing,
                  proteinG: recipe.proteinGPerServing,
                  carbsG: recipe.carbsGPerServing,
                  fatG: recipe.fatGPerServing,
                },
              },
            })
          }
        />
      ) : null}

      {target !== null ? (
        <QuantitySheet
          open
          onClose={() => setTarget(null)}
          target={target}
          date={date}
          meal={meal}
          mealLocked
          onSaved={backToDiary}
        />
      ) : null}

      <ManualSheet
        open={tab === 'manual'}
        onClose={() => setTab('search')}
        date={date}
        meal={meal}
        mealLocked
        onSaved={backToDiary}
      />
    </>
  );
}
